import { Box } from "@mui/material";
import {
  DataGrid,
  gridClasses,
  GridColDef,
  GridNoRowsOverlay,
  GridPaginationModel,
  GridRowParams,
  GridSortModel,
} from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { ImpactCell, TextCell } from "~/components/DataGridCells";
import { PolicyCheckNode } from "../../types";
import { ChecksTableComplianceCell } from "./ChecksTableComplianceCell";
import { ChecksTableStatusCell } from "./ChecksTableStatusCell";

interface ChecksTableProps {
  rows: PolicyCheckNode[];
  loading: boolean;
  policyId: string | undefined;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  rowCount: number;
  sortModel: GridSortModel;
  onSortModelChange: (model: GridSortModel) => void;
  basePath?: string;
  scopeParams?: URLSearchParams;
}

export function ChecksTable({
  rows,
  loading,
  policyId,
  paginationModel,
  onPaginationModelChange,
  rowCount,
  sortModel,
  onSortModelChange,
  basePath = "/policies",
  scopeParams,
}: ChecksTableProps) {
  const navigate = useNavigate();

  const columns: GridColDef<PolicyCheckNode>[] = [
    {
      field: "title",
      headerName: "Check",
      headerClassName: "left-cell",
      cellClassName: "left-cell",
      minWidth: 300,
      flex: 1,
      resizable: false,
      sortable: true,
      renderCell: (params) => (
        <TextCell {...params} TypographyProps={{ sx: { fontWeight: 700 } }} />
      ),
    },
    {
      field: "riskValue",
      headerName: "Risk",
      width: 150,
      resizable: false,
      align: "left",
      headerAlign: "left",
      sortable: true,
      renderCell: ImpactCell,
    },
    {
      field: "blastRadius",
      headerName: "Assets",
      width: 120,
      resizable: false,
      align: "left",
      headerAlign: "left",
      sortable: true,
      valueGetter: (value: PolicyCheckNode["blastRadius"]) =>
        value?.assets || 0,
      renderCell: TextCell,
    },
    {
      field: "enforcementState",
      headerName: "Status",
      width: 130,
      resizable: false,
      align: "left",
      headerAlign: "left",
      sortable: false,
      renderCell: ChecksTableStatusCell,
    },
    {
      field: "completion",
      headerName: "Check Compliance",
      headerClassName: "right-cell",
      cellClassName: "right-cell",
      width: 200,
      resizable: false,
      sortable: true,
      renderCell: ChecksTableComplianceCell,
    },
  ];

  const handleRowClick = (params: GridRowParams<PolicyCheckNode>) => {
    if (!policyId) return;
    // Navigate to check detail page
    const searchParams = new URLSearchParams(scopeParams);
    searchParams.set("findingMrn", params.row.findingMrn);
    navigate(
      `${basePath}/${encodeURIComponent(policyId)}/check?${searchParams}`,
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
      }}
    >
      <DataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        getRowId={(row) => row.findingMrn}
        columnHeaderHeight={54}
        rowHeight={54}
        disableRowSelectionOnClick
        disableColumnMenu
        onRowClick={handleRowClick}
        slots={{ noRowsOverlay: NoRowsOverlay }}
        slotProps={{
          loadingOverlay: {
            variant: "linear-progress",
            noRowsVariant: "skeleton",
          },
        }}
        sx={{
          borderRadius: 2,
          border: "none",
          backgroundColor: "background.paper",
          [`& .${gridClasses.columnSeparator}`]: {
            [`&:not(.${gridClasses["columnSeparator--resizable"]})`]: {
              display: "none",
            },
          },
          [`& .${gridClasses.columnHeader}`]: {
            ...tableHeaderStyles,
            backgroundColor: (theme) =>
              theme.palette.mode === "light" ? "#D9D9DA" : "background.dark",
            borderBottom: "none !important",
          },
          [`& .${gridClasses.sortIcon}`]: {
            fontSize: "10px",
            color: "text.primary",
          },
          [`& .${gridClasses.footerContainer}`]: {
            backgroundColor: (theme) =>
              theme.palette.mode === "light" ? "#D9D9DA" : "background.dark",
            borderTop: "none !important",
            backgroundImage: "none",
            opacity: 1,
          },
          // remove focus outline
          [`& .${gridClasses.columnHeader}:focus`]: {
            outline: "none",
          },
          [`& .${gridClasses.columnHeader}:focus-within`]: {
            outline: "none",
          },
          [`& .${gridClasses.cell}:focus`]: {
            outline: "none",
          },
          [`& .${gridClasses.cell}:focus-within`]: {
            outline: "none",
          },
          "& .MuiDataGrid-row--borderBottom": {
            borderBottom: "none",
          },
          "& .left-cell": {
            pl: 3,
          },
          "& .right-cell": {
            pr: 3,
          },
          "& .MuiDataGrid-row": {
            cursor: "pointer",
          },
        }}
        paginationMode="server"
        sortingMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        rowCount={rowCount}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        pageSizeOptions={[10, 25, 50]}
      />
    </Box>
  );
}

const tableHeaderStyles = {
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 700,
  lineHeight: "normal",
  color: "text.primary",
};

function NoRowsOverlay() {
  return (
    <GridNoRowsOverlay
      sx={{
        backgroundColor: "background.paper",
      }}
    />
  );
}
